"use client";

import { motion, AnimatePresence } from "framer-motion";
import Image from "next/image";
import { useState, useEffect, useCallback } from "react";

// Clean color palette - Only Deep Teal Green and White
const colors = {
  primary: "#395750",      // Deep Teal Green - Company color
  primaryDark: "#2A423C",  // Darker teal
  primaryLight: "#4B6B63", // Lighter teal
  white: "#FFFFFF",        // White
  whiteOff: "#F8F9FA",     // Off-white for subtle contrast
  textDark: "#1A2E29",     // Dark text for contrast
};

const murabahahSlides = [
    {
        id: 1,
        title: "خدمة لانا للتمويل الأصغر بدون فوائد",
        image: "/images/about/ifmb34.webp",
    },
//     {
//         id: 2,
//         title: "خدمة لانا للتمويل الأصغر بدون فوائد",
//         image: "/images/lana/bedru8.png",
//   "/images/about/ifmb34.webp",

//     }
];

const variants = {
  enter: (direction: number) => ({
    x: direction > 0 ? "100%" : "-100%",
    opacity: 0,
    scale: 0.92,
  }),
  center: {
    x: 0,
    opacity: 1,
    scale: 1,
  },
  exit: (direction: number) => ({
    x: direction > 0 ? "-100%" : "100%",
    opacity: 0,
    scale: 0.96,
  }),
};

export const MurabahahHeroSection = () => {
    const [[current, direction], setCurrentState] = useState([0, 0]);
    const [paused, setPaused] = useState(false);

    const goToNext = useCallback(() => {
        setCurrentState(([current]) => [(current + 1) % murabahahSlides.length, 1]);
    }, []);

    const goToPrev = useCallback(() => {
        setCurrentState(([current]) => [(current - 1 + murabahahSlides.length) % murabahahSlides.length, -1]);
    }, []);

    const goToSlide = useCallback((index: number) => {
        setCurrentState(([current]) => [index, index > current ? 1 : -1]);
    }, []);

    useEffect(() => {
        if (paused) return;
        const interval = setInterval(goToNext, 8000);
        return () => clearInterval(interval);
    }, [goToNext, paused]);

    return (
        <section className="relative mt-[92px] w-full overflow-hidden bg-transparent xl:mt-[108px]">
            {/* Banner - height follows the image's aspect ratio */}
            <div
                className="relative aspect-[4134/1379] w-full"
                onMouseEnter={() => setPaused(true)}
                onMouseLeave={() => setPaused(false)}
            >
                {/* Auto-sliding Banner Slides */}
                <AnimatePresence initial={false} custom={direction}>
                    <motion.div
                        key={current}
                        custom={direction}
                        variants={variants}
                        initial="enter"
                        animate="center"
                        exit="exit"
                        transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
                        className="absolute inset-0"
                    >
                        {/* Blurred Background for edge blending */}
                        <div className="absolute inset-0 z-0 blur-xl">
                            <Image
                                src={murabahahSlides[current].image}
                                alt="Blurred background"
                                fill
                                className="object-cover object-center"
                                quality={20}
                                priority
                            />
                            <div className="absolute inset-0 backdrop-blur-sm" style={{ background: `linear-gradient(to right, ${colors.primary}E6, ${colors.primary}B3)` }} />
                        </div>

                        {/* Main Image */}
                        <div className="relative z-10 h-full w-full">
                            <Image
                                src={murabahahSlides[current].image}
                                alt="Main slide"
                                fill
                                className="object-cover object-center"
                                priority
                            />
                            {/* Dark gradient overlay for bottom text readability */}
                            <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />
                        </div>

                        {/* Light sheen sweep across the banner as it enters */}
                        <motion.div
                            className="pointer-events-none absolute inset-y-0 w-1/3 bg-gradient-to-r from-transparent via-white/15 to-transparent z-20"
                            initial={{ x: "-150%" }}
                            animate={{ x: "400%" }}
                            transition={{ duration: 1.1, delay: 0.2, ease: "easeInOut" }}
                        />
                    </motion.div>
                </AnimatePresence>

                {/* YEGNA FOR ALL! Badge - Fixed to left bottom corner */}
                <div className="absolute bottom-4 left-4 z-30 sm:bottom-6 sm:left-6">
                    <motion.div
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ duration: 0.5, delay: 0.3 }}
                        whileHover={{ scale: 1.05 }}
                        className="group relative inline-block"
                    >
                        <div
                            className="rounded-lg px-3 py-1.5 text-xs font-bold tracking-wide text-white shadow-md transition-all duration-300 sm:px-5 sm:py-2.5 sm:text-base"
                            style={{
                                backgroundColor: colors.primary,
                                boxShadow: `0 4px 15px ${colors.primary}30`,
                            }}
                            onMouseEnter={(e) => {
                                e.currentTarget.style.backgroundColor = colors.white;
                                e.currentTarget.style.color = colors.primary;
                            }}
                            onMouseLeave={(e) => {
                                e.currentTarget.style.backgroundColor = colors.primary;
                                e.currentTarget.style.color = colors.white;
                            }}
                        >
                            YEGNA FOR ALL!
                            <motion.span
                                className="absolute inset-0 rounded-lg border-2 opacity-0 group-hover:opacity-100"
                                style={{ borderColor: colors.primary }}
                                initial={{ opacity: 0 }}
                                whileHover={{
                                    opacity: 1,
                                    borderColor: [colors.primary, `${colors.white}CC`, colors.primary],
                                }}
                                transition={{ duration: 1.5, repeat: Infinity, repeatType: "reverse" }}
                            />
                        </div>
                        <motion.div
                            className="absolute -inset-1 -z-10 rounded-lg transition-all duration-500"
                            style={{ backgroundColor: `${colors.primary}20` }}
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                        />
                    </motion.div>
                </div>

                {/* Static Text at Bottom Right (Above Footer) */}
                <div className="absolute bottom-4 right-4 z-30 sm:bottom-6 sm:right-6">
                    <AnimatePresence mode="wait">
                        <motion.div
                            key={murabahahSlides[current].id}
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -10 }}
                            transition={{ duration: 0.5 }}
                            className="text-right"
                        >
                            <h2 className="text-lg font-bold tracking-wide text-white drop-shadow-lg sm:text-xl md:text-2xl lg:text-3xl">
                                {murabahahSlides[current].title}
                            </h2>
                        </motion.div>
                    </AnimatePresence>
                </div>

                {/* Navigation Arrows */}
                <div className="pointer-events-none absolute inset-0 z-20 flex items-center justify-between px-2 sm:px-4">
                    <button
                        onClick={goToPrev}
                        className="pointer-events-auto rounded-full bg-black/20 p-1.5 text-white backdrop-blur-sm transition-all duration-300 hover:bg-white/30 sm:p-2 md:p-3"
                        aria-label="Previous slide"
                    >
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 sm:h-6 sm:w-6 md:h-8 md:w-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                        </svg>
                    </button>
                    <button
                        onClick={goToNext}
                        className="pointer-events-auto rounded-full bg-black/20 p-1.5 text-white backdrop-blur-sm transition-all duration-300 hover:bg-white/30 sm:p-2 md:p-3"
                        aria-label="Next slide"
                    >
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 sm:h-6 sm:w-6 md:h-8 md:w-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                        </svg>
                    </button>
                </div>

                {/* Indicators */}
                <div className="absolute bottom-3 left-0 right-0 z-20 flex justify-center gap-1.5 sm:bottom-6 sm:gap-2">
                    {murabahahSlides.map((slide, index) => (
                        <motion.button
                            key={slide.id}
                            onClick={() => goToSlide(index)}
                            className="h-1.5 rounded-full transition-all duration-300 sm:h-2"
                            style={{
                                backgroundColor: current === index ? colors.white : `${colors.white}80`,
                            }}
                            aria-label={`Go to slide ${index + 1}`}
                            whileHover={{ scale: 1.3 }}
                            animate={{
                                width: current === index ? 16 : 6,
                            }}
                            transition={{ duration: 0.3 }}
                        />
                    ))}
                </div>

                {/* Wave pattern at bottom */}
                <div className="absolute bottom-0 left-0 right-0 z-10 h-16 bg-[url('/images/wave-pattern.svg')] bg-repeat-x opacity-80"></div>
            </div>
        </section>
    );
};